/**
 * EcosystemGraphView: the platform apex's "ecosystem of ecosystems" graph.
 *
 * Presentational view rendered by EcosystemsPage (the `?view=graph` tab).
 * Receives the already-fetched ecosystem list as a prop: no fetching, no
 * page shell here. Extracted from the former standalone EcosystemGraphPage
 * (`/ecosystems/graph`) when the card and graph views were merged behind
 * one route (#241).
 *
 * Each hosted ecosystem is rendered as a single node in a force-directed
 * graph; clicking a node navigates into that ecosystem in path mode
 * (`/{slug}`). Built on `react-force-graph-2d`, the same library the
 * tenant-level `features/graph/` components use.
 *
 * EDGES ARE DEFERRED. There is no cross-ecosystem connection model yet
 * (`EcosystemConnection` is TBD and would be meaningless with a single hosted
 * ecosystem). The graph data is therefore shaped as `{ nodes, links }` with
 * `links` always an empty array for now: `react-force-graph-2d` natively
 * accepts that shape, so when a connection model lands, populating `links`
 * from a future API field is the only change needed here: no restructuring.
 * No fake/placeholder edges are fabricated.
 *
 * Single-node safety: with COSTEM as the only hosted ecosystem the graph
 * renders exactly one node and zero links. ForceGraph2D handles that without
 * a divide-by-zero or empty-links crash; `onEngineStop` pins a sensible fixed
 * zoom for the 1-node case instead of `zoomToFit` (which over-zooms on a
 * single point).
 */
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import ForceGraph2D from 'react-force-graph-2d';
import { Card } from 'react-bootstrap';

import { ecosystemNodeColor } from './ecosystemDisplay';
import './EcosystemsPage.css';

// Canvas height in pixels. Width is measured from the container on mount.
const GRAPH_HEIGHT = 560;

// Fixed zoom level applied when the graph contains a single node: zoomToFit
// over-zooms on a lone point, so we pin a readable scale instead.
const SINGLE_NODE_ZOOM = 4;

export default function EcosystemGraphView({ ecosystems }) {
    const graphRef = useRef();
    const containerRef = useRef();

    const [hoveredNode, setHoveredNode] = useState(null);
    const [width, setWidth] = useState(800);

    // --- Responsive width ---
    // The parent only mounts this view once data is loaded and the graph tab
    // is active, so the container exists on first effect run.
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // --- Graph data shape ---
    // `{ nodes, links }`. `links` is deliberately always empty: no
    // cross-ecosystem connection model exists yet. The shape is final, so a
    // future API edge array drops straight into `links` with no rewrite.
    const graphData = useMemo(
        () => ({
            nodes: ecosystems.map((eco) => ({
                id: eco.slug,
                name: eco.name,
                shortName: eco.shortName || eco.name,
                memberCount: eco.memberCount || 0,
                color: ecosystemNodeColor(eco),
            })),
            links: [],
        }),
        [ecosystems]
    );

    // --- Node click → navigate into the ecosystem (path mode /{slug}) ---
    // v1.0.1 #49: SPA push (`navigate(`/${slug}`)`) does NOT re-bootstrap
    // EcosystemContext: the provider reads `window.location` only on its
    // initial mount. From the platform apex, a SPA push to `/{slug}` therefore
    // leaves the context in platform mode while React Router renders a
    // tenant-shaped path: nothing matches, the user sees a 404. Force a full
    // page reload via `window.location.href` so EcosystemContext re-runs its
    // boot resolution against the new URL and the new tenant comes up clean.
    const handleNodeClick = useCallback((node) => {
        if (node?.id) {
            window.location.href = `/${node.id}`;
        }
    }, []);

    const handleNodeHover = useCallback((node) => {
        setHoveredNode(node ? node.id : null);
    }, []);

    // --- Custom node renderer (Canvas 2D) ---
    const nodeCanvasObject = useCallback(
        (node, ctx, globalScale) => {
            const size = 10;
            const isHovered = hoveredNode === node.id;

            // Filled circle in the ecosystem's brand color.
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = node.color;
            ctx.fill();

            // Gold hover ring.
            if (isHovered) {
                ctx.strokeStyle = '#FFD700';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            // Always-visible label below the node: the apex graph has few
            // nodes, so labels never clutter the canvas.
            const fontSize = Math.max(12 / globalScale, 3);
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#333';
            ctx.fillText(node.name, node.x, node.y + size + fontSize);
        },
        [hoveredNode]
    );

    // After the simulation settles, frame the graph. zoomToFit over-zooms on
    // a single point, so the 1-node case gets a fixed zoom instead.
    const handleEngineStop = useCallback(() => {
        if (!graphRef.current) return;
        if (graphData.nodes.length <= 1) {
            graphRef.current.centerAt(0, 0, 400);
            graphRef.current.zoom(SINGLE_NODE_ZOOM, 400);
        } else {
            graphRef.current.zoomToFit(400, 60);
        }
    }, [graphData.nodes.length]);

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body
                className="p-0 ecosystem-graph-page-canvas"
                ref={containerRef}
            >
                <ForceGraph2D
                    ref={graphRef}
                    graphData={graphData}
                    width={width}
                    height={GRAPH_HEIGHT}
                    nodeCanvasObject={nodeCanvasObject}
                    nodePointerAreaPaint={(node, color, ctx) => {
                        ctx.fillStyle = color;
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, 12, 0, 2 * Math.PI);
                        ctx.fill();
                    }}
                    onNodeClick={handleNodeClick}
                    onNodeHover={handleNodeHover}
                    cooldownTicks={100}
                    onEngineStop={handleEngineStop}
                    enableNodeDrag={true}
                    enableZoomInteraction={true}
                    enablePanInteraction={true}
                />
            </Card.Body>
            <Card.Footer className="bg-white border-top text-muted small">
                {graphData.nodes.length}{' '}
                {graphData.nodes.length === 1 ? 'ecosystem' : 'ecosystems'}.
                Select a node to explore that ecosystem. Connections between
                ecosystems are not yet modeled.
            </Card.Footer>
        </Card>
    );
}

EcosystemGraphView.propTypes = {
    ecosystems: PropTypes.arrayOf(
        PropTypes.shape({
            slug: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            shortName: PropTypes.string,
            memberCount: PropTypes.number,
        })
    ).isRequired,
};
